import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import {
  Add,
  ContentCopy,
  Delete,
  Edit,
  MeetingRoom,
  MoreVert,
  Visibility,
} from '@mui/icons-material';
import StudioSelector, { StudioOption } from '../components/StudioSelector';
import { useAuth } from '../contexts/AuthContext';
import SeatLayoutEditor from '../components/seat-layout/SeatLayoutEditor';
import SeatMapCanvas from '../components/seat-layout/SeatMapCanvas';
import seatLayoutStore from '../services/seatLayoutStore';
import { colors } from '../theme/theme';
import {
  SeatLayout,
  bookableCount,
  oosCount,
  surchargedRows,
} from '../types/seatLayout';
import { STUDIO_SCOPED_ROLES } from '../utils/roles';
import { useMyLocations } from '../hooks/useMyLocations';
import BranchSelect from '../components/BranchSelect';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  ROOMS & LAYOUTS
 * ─────────────────────────────────────────────────────────────────────────────
 * Studios define the physical shape of a room — rows of bikes, walkways,
 * out-of-service machines — which a class will later be linked to so members can
 * pick a specific bike, movie-theatre style.
 *
 * Rooms live behind `studioPermissions.enableSeatSelection`. A studio without it
 * never sees this page — the sidebar hides the tab (Layout.tsx) and the guard
 * below catches a bookmarked URL. The backend refuses too
 * (seat-layouts.service `assertEnabled`), so the flag holds at three layers.
 *
 * Super admins are exempt: they own the studio picker above and are the ones who
 * turn the flag on.
 */

const SeatLayoutsPage: React.FC = () => {
  const { admin } = useAuth();
  const isStudioRole = STUDIO_SCOPED_ROLES.includes(
    admin?.role ?? '',
  );
  const seatSelectionEnabled =
    (admin?.studio as any)?.studioPermissions?.enableSeatSelection === true;
  const blocked = isStudioRole && !seatSelectionEnabled;

  const [selectedStudio, setSelectedStudio] = useState<StudioOption | null>(
    null,
  );
  const [layouts, setLayouts] = useState<SeatLayout[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [preview, setPreview] = useState<SeatLayout | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SeatLayout | null>(null);
  const [menu, setMenu] = useState<{
    anchor: HTMLElement;
    layout: SeatLayout;
  } | null>(null);
  const [toast, setToast] = useState<{
    msg: string;
    severity: 'success' | 'error';
  } | null>(null);

  // ── WHICH LOCATION'S ROOMS ──────────────────────────────────────────────
  // StudioSelector seeds a studio role with the branch it signed in as. Under
  // "All locations" that is a silent default — rooms listed, created and
  // edited at one branch while the operator believes they are looking at the
  // brand — and every write is refused anyway (__all__). So ask.
  const { isAllLocations } = useMyLocations();
  const [branch, setBranch] = useState('');
  const pickBranch = isStudioRole && isAllLocations;
  const studioId = pickBranch ? branch : selectedStudio?._id ?? '';

  const load = useCallback(async () => {
    // Don't fire a request we know the backend will refuse.
    if (!studioId || blocked) {
      setLayouts([]);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      setLayouts(await seatLayoutStore.list(studioId));
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ?? err?.message ?? 'Could not load rooms';
      setLoadError(msg);
      setLayouts([]);
    } finally {
      setLoading(false);
    }
  }, [studioId, blocked]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    if (!studioId) return;
    setEditing(
      seatLayoutStore.draft(studioId, pickBranch ? undefined : selectedStudio?.studioName),
    );
    setEditorOpen(true);
  };

  const openEdit = (layout: SeatLayout) => {
    setEditing(layout);
    setEditorOpen(true);
  };

  const handleSave = async (draft: any) => {
    setSaving(true);
    try {
      if (draft._id) {
        const { _id, studio, createdAt, updatedAt, ...patch } = draft;
        await seatLayoutStore.update(studioId, _id, patch);
        setToast({ msg: 'Room saved', severity: 'success' });
      } else {
        await seatLayoutStore.create(draft);
        setToast({ msg: 'Room created', severity: 'success' });
      }
      setEditorOpen(false);
      setEditing(null);
      await load();
    } catch (err: any) {
      setToast({ msg: err?.message ?? 'Could not save the room', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (layout: SeatLayout) => {
    try {
      await seatLayoutStore.duplicate(studioId, layout._id);
      setToast({ msg: `Copied "${layout.name}"`, severity: 'success' });
      await load();
    } catch (err: any) {
      setToast({ msg: err?.message ?? 'Could not copy the room', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await seatLayoutStore.remove(studioId, deleteTarget._id);
      setToast({ msg: `Deleted "${deleteTarget.name}"`, severity: 'success' });
      setDeleteTarget(null);
      await load();
    } catch (err: any) {
      setToast({ msg: err?.message ?? 'Could not delete the room', severity: 'error' });
    }
  };

  // Placed after every hook so the hook order stays identical on both branches.
  if (blocked) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} mb={2}>
          Rooms &amp; Layouts
        </Typography>
        <Alert severity="info">
          Seat selection isn't enabled for your studio, so your sessions are
          booked by headcount and there's no room to lay out. Contact Sweat
          Social if you'd like members to pick a specific bike or reformer.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Rooms &amp; Layouts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define the physical shape of a room so members can pick their bike.
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1.5}>
          <StudioSelector
            value={selectedStudio}
            onChange={setSelectedStudio}
            required
            minWidth={240}
          />
          {pickBranch && (
            <Box minWidth={240}>
              <BranchSelect value={branch} onChange={setBranch} minRole="manager" />
            </Box>
          )}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openNew}
            disabled={!studioId}
          >
            New room
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Attach a room to a <strong>class</strong> and every session of that class
        derives its capacity from the room's bookable machines, and members pick a
        specific one. Requires <strong>Enable Seat Selection</strong> on the studio.
      </Alert>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 2 }}>{loadError}</Alert>
      )}

      {/* Body */}
      {!studioId ? (
        <EmptyState
          title={pickBranch ? 'Pick a location' : 'Pick a studio'}
          body="Rooms belong to a studio. Choose one above to see or create its layouts."
        />
      ) : loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : layouts.length === 0 ? (
        <EmptyState
          title="No rooms yet"
          body={`${selectedStudio?.studioName ?? 'This studio'} has no layouts. Create one — a spin room, a reformer room — then link it to a class.`}
          action={
            <Button variant="contained" startIcon={<Add />} onClick={openNew}>
              New room
            </Button>
          }
        />
      ) : (
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          }}
          gap={2}
        >
          {layouts.map((layout) => {
            const bookable = bookableCount(layout);
            const oos = oosCount(layout);
            const surcharged = surchargedRows(layout);
            return (
              <Card
                key={layout._id}
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  transition: 'box-shadow .15s',
                  '&:hover': { boxShadow: 3 },
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={1}
                  >
                    <Box minWidth={0}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {layout.name}
                      </Typography>
                      {layout.description && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {layout.description}
                        </Typography>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) =>
                        setMenu({ anchor: e.currentTarget, layout })
                      }
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Box>

                  <MiniMap layout={layout} />

                  <Box display="flex" gap={0.75} flexWrap="wrap" mt={1.5}>
                    <Chip
                      size="small"
                      label={`${bookable} bikes`}
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${layout.rows.length} rows`}
                    />
                    {oos > 0 && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={`${oos} out of service`}
                      />
                    )}
                    {surcharged.map((r) => (
                      <Chip
                        key={r.rowId}
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={`Row ${r.label} +₹${r.surcharge}`}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Card menu */}
      <Menu
        open={Boolean(menu)}
        anchorEl={menu?.anchor ?? null}
        onClose={() => setMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (menu) openEdit(menu.layout);
            setMenu(null);
          }}
        >
          <Edit fontSize="small" style={{ marginRight: 8 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) setPreview(menu.layout);
            setMenu(null);
          }}
        >
          <Visibility fontSize="small" style={{ marginRight: 8 }} /> Preview
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) handleDuplicate(menu.layout);
            setMenu(null);
          }}
        >
          <ContentCopy fontSize="small" style={{ marginRight: 8 }} /> Duplicate
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) setDeleteTarget(menu.layout);
            setMenu(null);
          }}
          sx={{ color: colors.error }}
        >
          <Delete fontSize="small" style={{ marginRight: 8 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Editor */}
      {editing && (
        <SeatLayoutEditor
          open={editorOpen}
          initial={editing}
          saving={saving}
          onClose={() => {
            setEditorOpen(false);
            setEditing(null);
          }}
          onSave={handleSave}
        />
      )}

      {/* Read-only preview — what the member picker will be drawn from */}
      <Dialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{preview?.name}</DialogTitle>
        <DialogContent dividers>
          {preview && (
            <SeatMapCanvas
              rows={preview.rows}
              frontLabel={preview.frontLabel}
              backLabel={preview.backLabel}
              orientation={preview.orientation}
              readOnly
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete "{deleteTarget?.name}"?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The layout will be removed from this browser.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Once rooms are linked to classes, deleting one that has future
            bookings will be blocked instead — bikes people already reserved
            cannot vanish.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity ?? 'success'} onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/** Dot-grid thumbnail: enough to recognise a room's shape at a glance. */
const MiniMap: React.FC<{ layout: SeatLayout }> = ({ layout }) => (
  <Box
    mt={1.5}
    p={1.5}
    display="flex"
    flexDirection="column"
    gap={0.5}
    alignItems="center"
    sx={{
      borderRadius: 2,
      background: colors.backgroundTertiary,
      border: `1px solid ${colors.divider}`,
      minHeight: 76,
      justifyContent: 'center',
    }}
  >
    {layout.rows.slice(0, 8).map((row) => (
      <Box key={row.rowId} display="flex" gap={0.4} justifyContent="center">
        {row.seats.slice(0, 20).map((slot) => (
          <Box
            key={slot.seatId}
            sx={{
              width: 7,
              height: 7,
              borderRadius: 0.5,
              background:
                slot.state === 'gap'
                  ? 'transparent'
                  : slot.state === 'oos'
                    ? colors.greyText3
                    : row.surcharge > 0
                      ? colors.primary
                      : colors.greyText2,
              border:
                slot.state === 'gap'
                  ? `1px dashed ${colors.greyText3}`
                  : undefined,
            }}
          />
        ))}
      </Box>
    ))}
  </Box>
);

const EmptyState: React.FC<{
  title: string;
  body: string;
  action?: React.ReactNode;
}> = ({ title, body, action }) => (
  <Stack alignItems="center" spacing={1.5} py={8}>
    <MeetingRoom sx={{ fontSize: 44, color: colors.greyText3 }} />
    <Typography variant="h6">{title}</Typography>
    <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={420}>
      {body}
    </Typography>
    {action}
  </Stack>
);

export default SeatLayoutsPage;
